/**
 * Security utilities to protect the application from unauthorized access and copying
 */

// Add Firefox-specific property to Window interface
declare global {
  interface Window {
    mozInnerScreenX?: number;
  }
}

/**
 * Detects if developer tools are open
 */
export const detectDevTools = (): boolean => {
  const threshold = 160;
  
  // Check for window size difference (common dev tools detection)
  const widthThreshold = window.outerWidth - window.innerWidth > threshold;
  const heightThreshold = window.outerHeight - window.innerHeight > threshold;
  
  // Check for Firefox dev tools
  const firefoxDevtools = 
    /firefox/i.test(navigator.userAgent) && 
    /Gecko/i.test(navigator.userAgent) && 
    window.mozInnerScreenX !== undefined && 
    Math.abs(window.outerWidth - (window.innerWidth + window.mozInnerScreenX)) > threshold;
  
  return widthThreshold || heightThreshold || firefoxDevtools;
};

/**
 * Obfuscates sensitive data in the application
 */
export const obfuscateData = (data: unknown): unknown => {
  if (typeof data !== 'object' || data === null) {
    return data;
  }
  
  // Create a copy to avoid modifying the original
  const result = Array.isArray(data) ? [...data] : {...data};
  
  // Obfuscate specific sensitive fields
  const sensitiveFields = ['materialPerKgRates', 'printingRates', 'dieMakingCost', 'shippingRates'];
  
  for (const key in result) {
    if (sensitiveFields.includes(key)) {
      // Add a random factor to obfuscate the actual values
      if (typeof result[key] === 'number') {
        const randomFactor = 0.98 + Math.random() * 0.04; // Random factor between 0.98 and 1.02
        result[key] = result[key] * randomFactor;
      } else if (typeof result[key] === 'object' && result[key] !== null) {
        result[key] = obfuscateData(result[key]);
      }
    } else if (typeof result[key] === 'object' && result[key] !== null) {
      result[key] = obfuscateData(result[key]);
    }
  }
  
  return result;
};

/**
 * Initializes security measures
 */
export const initSecurity = (): void => {
  // Check for dev tools periodically (reduced frequency to minimize performance impact)
  // NOTE: Dev tools detection disabled in production to avoid false positives and performance issues
  // Only log a warning instead of reloading to prevent disruption
  if (process.env.NODE_ENV === 'development') {
    setInterval(() => {
      if (detectDevTools()) {
        console.warn('Developer tools detected');
      }
    }, 30000); // Check every 30 seconds instead of 1 second
  }
  
  // Disable various browser features that could be used to extract information
  document.addEventListener('dragstart', e => e.preventDefault()); // Prevent dragging elements
  document.addEventListener('selectstart', e => e.preventDefault()); // Prevent text selection
  document.addEventListener('contextmenu', e => e.preventDefault()); // Prevent right-click context menu
  
  // Important: Do NOT clear all storage on unload.
  // Clearing storage here wipes Supabase auth tokens and logs users out on refresh.
  // If you need to clear ephemeral app data, do it selectively elsewhere.
  
  // Override console methods to prevent logging sensitive data
  const originalConsoleLog = console.log;
  const originalConsoleError = console.error;
  const originalConsoleWarn = console.warn;
  const originalConsoleInfo = console.info;
  
  console.log = function(...args) {
    // Filter sensitive information before logging
    const filteredArgs = args.map(arg => 
      typeof arg === 'object' && arg !== null ? '[REDACTED]' : arg
    );
    originalConsoleLog.apply(console, filteredArgs);
  };
  
  console.error = function(...args) {
    const filteredArgs = args.map(arg => 
      typeof arg === 'object' && arg !== null ? '[REDACTED]' : arg
    );
    originalConsoleError.apply(console, filteredArgs);
  };
  
  console.warn = function(...args) {
    const filteredArgs = args.map(arg => 
      typeof arg === 'object' && arg !== null ? '[REDACTED]' : arg
    );
    originalConsoleWarn.apply(console, filteredArgs);
  };
  
  console.info = function(...args) {
    const filteredArgs = args.map(arg => 
      typeof arg === 'object' && arg !== null ? '[REDACTED]' : arg
    );
    originalConsoleInfo.apply(console, filteredArgs);
  };
}; 
